/**
 * The relay address, and the token that must not leak.
 *
 * Pure: strings in, strings out, no DOM, no globals (REQ-NFR-4). This is the **only** module that
 * ever holds the token — everything else takes a {@link RelayBase} and asks for a URL, or asks for
 * {@link relayHost} when something has to reach a human.
 *
 * Implements the page half of contract 08 §2.1.
 */
import { ok, err, type Result } from './result';

/** A validated relay base: `https://<host>/t/<token>`, normalised, no trailing slash (08 §2.1). */
export type RelayBase = { readonly url: string };

export type RelayError =
  /** No `#r=` at all — the ordinary offline case, and not something to report. */
  | { kind: 'absent' }
  /** Present but unusable. `why` says what rule failed and **never** echoes the value. */
  | { kind: 'malformed'; why: string };

/**
 * The token as this page will accept it. `secrets.token_urlsafe(16)` emits 22 characters from
 * `[A-Za-z0-9_-]`; the range is wide enough to survive the server minting longer ones and narrow
 * enough to catch a truncated paste. 08 §2.1 fixes the generator but not the bounds, so this is
 * the page's own reading — deliberately permissive on length, strict on the character class.
 */
const TOKEN_RE = /^[A-Za-z0-9_-]{16,64}$/;

/**
 * The single host the http exception may name. Literally `127.0.0.1`, not `localhost`: the two are
 * different origins to a browser, and `localhost` may resolve to `::1` first, where the relay —
 * which binds 127.0.0.1 — is not listening. That failure is silent, so it is refused here instead.
 */
const BENCH_HOST = '127.0.0.1';

const fail = (why: string): Result<RelayBase, RelayError> => err({ kind: 'malformed', why });

/**
 * Read the relay base out of a URL fragment.
 *
 * `pageScheme` is a parameter rather than a read of `location.protocol` for two reasons: it keeps
 * this module pure, and it makes the http bench exception impossible to take by accident on the
 * deployed page — where it would be a downgrade to plaintext over LTE.
 *
 * @param hash `location.hash`, with or without its leading `#`.
 * @param pageScheme the scheme the page itself was served over.
 */
export function parseRelayBase(
  hash: string,
  pageScheme: 'http:' | 'https:',
): Result<RelayBase, RelayError> {
  // URLSearchParams percent-decodes for us — and also decodes `+` as a space. A token_urlsafe
  // value never contains `+`, so nothing valid is lost; what matters is that every check below
  // runs on the DECODED string, so a mangled paste fails here rather than reaching `new URL`.
  const raw = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash).get('r');
  if (raw === null) return err({ kind: 'absent' });
  if (raw === '') return fail('the relay address is empty');

  let u: URL;
  try {
    u = new URL(raw); // absolute only; a relative value throws
  } catch {
    return fail('the relay address is not an absolute URL');
  }

  if (u.username !== '' || u.password !== '') return fail('the relay address carries userinfo');
  if (u.search !== '') return fail('the relay address carries a query');
  if (u.hash !== '') return fail('the relay address carries a nested fragment');

  if (u.protocol === 'http:') {
    // Bench only, and both gates must hold: the page itself is http (so we are behind
    // `adb reverse`, not on the deployed origin) and the host is the loopback literal.
    if (pageScheme !== 'http:') return fail('an http relay is only allowed on an http page');
    if (u.hostname !== BENCH_HOST) return fail(`an http relay is only allowed on ${BENCH_HOST}`);
  } else if (u.protocol !== 'https:') {
    return fail('the relay address must be https');
  }

  // Exactly `/t/<token>`: split gives ['', 't', token] and nothing else, so a trailing slash —
  // which yields a fourth, empty element — is refused along with any deeper path.
  const parts = u.pathname.split('/');
  if (parts.length !== 3 || parts[0] !== '' || parts[1] !== 't') {
    return fail('the relay path must be /t/<token>');
  }
  if (!TOKEN_RE.test(parts[2] ?? '')) return fail('the relay token is not the expected shape');

  return ok({ url: `${u.protocol}//${u.host}${u.pathname}` });
}

/**
 * GET target for the stream. `lastId` is passed on **every** connect, not only the browser's own
 * automatic reconnect (08 §6.2) — the browser sends `Last-Event-ID` for its reconnects and for
 * nothing else, and the watchdog's reconnect constructs a fresh `EventSource`, which is exactly
 * the case the header does not cover.
 */
export function eventsUrl(base: RelayBase, lastId: string | null): string {
  const q = lastId ? `?last=${encodeURIComponent(lastId)}` : '';
  return `${base.url}/events${q}`;
}

/** POST target for board output, verbatim (08 §2.2). */
export function outUrl(base: RelayBase): string {
  return `${base.url}/out`;
}

/** POST target for the page's own answers. Never board bytes (08 §2.2). */
export function noteUrl(base: RelayBase): string {
  return `${base.url}/note`;
}

/**
 * Host and port only — the one part of the base that may reach the screen or a log record
 * (08 §2.1, REQ-REM-3). It exists so the banner can read *"REMOTE · abc-def.trycloudflare.com"*
 * out loud over a phone call without reading out the token.
 */
export function relayHost(base: RelayBase): string {
  return new URL(base.url).host;
}
